import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useYear } from '../contexts/YearContext';
import {
  Activity,
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus,
  Wallet,
  Flame,
  Waves,
  PiggyBank,
} from 'lucide-react';

/**
 * Dashboard "Plan Health" card.
 *
 * Single composite read on whether income, expenses, and Buffer are
 * supporting the user's current plan. Pure read-only — every metric is
 * sourced from the backend's existing trusted helpers via
 * `GET /api/dashboard/plan-health?year=YYYY`.
 *
 *   • True Minimum Income       ← financial_plan.recommended_weekly_target
 *   • Current Weekly Avg Income ← compute_total_business_income / weeks
 *   • Business Burn Rate        ← Σ business expenses / weeks
 *   • Buffer Status             ← compute_canonical_balances.buffer
 *   • Net Profit YTD            ← total income − total expenses
 *
 * No new math. No allocation/canonical/writer/export side-effects.
 */

const STATUS_STYLES = {
  green: {
    pill: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    accent: 'from-emerald-900/30 to-slate-900/40 border-emerald-900/40',
    dot: 'bg-emerald-400',
  },
  yellow: {
    pill: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    accent: 'from-amber-900/30 to-slate-900/40 border-amber-900/40',
    dot: 'bg-amber-400',
  },
  red: {
    pill: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
    accent: 'from-rose-900/30 to-slate-900/40 border-rose-900/40',
    dot: 'bg-rose-400',
  },
};

const fmt = (v) =>
  `$${Number(v || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function MetricCard({ label, helper, value, icon: Icon, valueClassName, trendIcon: TrendIcon, testId }) {
  return (
    <div
      className="bg-slate-900/85 border border-slate-700/60 rounded-xl p-4 flex flex-col"
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-gray-300">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
      </div>
      <div className="flex items-baseline gap-1.5 mb-1.5">
        <span className={`text-xl font-bold ${valueClassName || 'text-white'}`} data-testid={`${testId}-value`}>
          {value}
        </span>
        {TrendIcon && <TrendIcon className="w-3.5 h-3.5 text-gray-400" />}
      </div>
      <p className="text-xs text-gray-400 leading-snug">{helper}</p>
    </div>
  );
}

function PlanHealthCard() {
  const { selectedYear } = useYear();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/dashboard/plan-health`, {
          params: { year: selectedYear },
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error('Plan Health load failed:', err);
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedYear]);

  const status = STATUS_STYLES[data?.status] || STATUS_STYLES.yellow;
  const m = data?.metrics || {};
  const b = data?.breakdown || {};

  // Trend icons reflect direction relative to plan, not period-over-period.
  const incomeTrend =
    m.current_weekly_avg_income >= m.true_minimum_income
      ? TrendingUp
      : m.current_weekly_avg_income > 0
      ? TrendingDown
      : Minus;
  const profitTrend =
    m.net_profit_ytd > 0 ? TrendingUp : m.net_profit_ytd < 0 ? TrendingDown : Minus;

  return (
    <div
      className={`bg-gradient-to-br ${status.accent} rounded-2xl shadow-xl p-6 border`}
      data-testid="plan-health-card"
    >
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="flex items-center space-x-2 mb-1">
            <Activity className="w-5 h-5 text-white/80" />
            <h2 className="text-lg font-semibold text-white">Plan Health</h2>
          </div>
          <p className="text-sm text-gray-400 max-w-xl">
            Shows whether your income, expenses, and Buffer are supporting your current plan · {selectedYear}
          </p>
        </div>

        {!loading && data && (
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-bold tracking-wide uppercase shadow-sm ${status.pill}`}
            data-testid="plan-health-status-pill"
          >
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${status.dot}`} />
            <span data-testid="plan-health-status-label">{data.status_label}</span>
          </div>
        )}
      </div>

      {loading && (
        <div className="h-40 flex items-center justify-center text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {!loading && !data && (
        <div className="h-32 flex items-center justify-center text-gray-500 text-sm">
          Plan Health unavailable.
        </div>
      )}

      {!loading && data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="plan-health-metrics">
            <MetricCard
              label="True Minimum Income"
              helper="Minimum weekly income needed to fund your plan."
              value={fmt(m.true_minimum_income)}
              icon={Wallet}
              testId="plan-health-true-minimum"
            />
            <MetricCard
              label="Current Weekly Avg"
              helper="Average weekly income from advances, overrides, and monthly checks."
              value={fmt(m.current_weekly_avg_income)}
              icon={TrendingUp}
              trendIcon={incomeTrend}
              valueClassName={
                m.current_weekly_avg_income >= m.true_minimum_income
                  ? 'text-emerald-300'
                  : 'text-amber-300'
              }
              testId="plan-health-avg-income"
            />
            <MetricCard
              label="Business Burn Rate"
              helper="Average weekly business expenses."
              value={fmt(m.business_burn_rate)}
              icon={Flame}
              testId="plan-health-burn"
            />
            <MetricCard
              label="Buffer Status"
              helper={
                b.buffer_label
                  ? `Unallocated capital available to absorb swings · ${b.buffer_label}.`
                  : 'Unallocated capital available to absorb swings.'
              }
              value={fmt(m.buffer_status)}
              icon={Waves}
              valueClassName={
                m.buffer_status > 0
                  ? 'text-cyan-300'
                  : m.buffer_status < 0
                  ? 'text-rose-300'
                  : 'text-gray-300'
              }
              testId="plan-health-buffer"
            />
            <MetricCard
              label="Net Profit YTD"
              helper="Total income minus total expenses."
              value={fmt(m.net_profit_ytd)}
              icon={PiggyBank}
              trendIcon={profitTrend}
              valueClassName={
                m.net_profit_ytd > 0
                  ? 'text-emerald-300'
                  : m.net_profit_ytd < 0
                  ? 'text-rose-300'
                  : 'text-gray-300'
              }
              testId="plan-health-net-profit"
            />
          </div>

          {data.status_message && (
            <p
              className="mt-4 text-sm text-gray-200 leading-relaxed"
              data-testid="plan-health-status-message"
            >
              {data.status_message}
            </p>
          )}

          {/* Recommended action — short, decision-first copy keyed to status. */}
          {(() => {
            const actions = {
              green:
                'Action: Hold steady or scale carefully.',
              yellow:
                'Action: Monitor Buffer, tighten expenses, or increase weekly income.',
              red:
                'Action: Increase weekly income, reduce business spend, or update your plan assumptions.',
            };
            const action = actions[data.status] || actions.yellow;
            return (
              <div
                className={`mt-3 px-3 py-2 rounded-lg border text-sm font-medium ${
                  data.status === 'green'
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'
                    : data.status === 'red'
                    ? 'bg-rose-500/10 border-rose-500/30 text-rose-200'
                    : 'bg-amber-500/10 border-amber-500/30 text-amber-200'
                }`}
                data-testid="plan-health-action"
              >
                {action}
              </div>
            );
          })()}

          <div className="mt-3 flex items-center justify-between flex-wrap gap-2 text-xs text-gray-400">
            <span data-testid="plan-health-footer">
              Use this card to decide whether to hold steady, tighten expenses, or scale.
            </span>
            {b.income_vs_target_pct != null && (
              <span data-testid="plan-health-pct">
                Income pace · <strong className="text-gray-200">{b.income_vs_target_pct}%</strong> of target
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default PlanHealthCard;
